import type { Ring, Vec2 } from '../geo/types';
import type { Landmark, PaletteModel } from '../data/types';
import type { SiteModel } from '../site/types';
import { createBoatWater, type BoatWater } from '../nav/boatWater';
import type { Obstacle } from './types';
import { BOAT, WORLD } from '../core/config';
import { pointInRing, ringBounds } from '../geo/polygon';
import { boundsRing, obstacleProbe } from '../nav/buildCollisionWorld';
import { createSlidingWorld } from '../nav/slidingWorld';
import { bridgeBarriers, type Levels } from './terrain/bridgeBarriers';
import { damBarriers } from './fabric/landmarks/damBarriers';
import { waterGateBarriers } from './fabric/landmarks/waterGateBarriers';

export interface BoatWorldInput {
  readonly site: SiteModel;
  readonly palette: PaletteModel;
  /** Dams and water gates provide their own geometry for the passage beneath them. */
  readonly landmarks: readonly Landmark[];
  /** What the features stand on the ground: only what reaches the water stops a boat. */
  readonly obstacles: readonly Obstacle[];
  readonly afloat: readonly Obstacle[];
}

/** A hull lies between the water and its gunwale; its crew sit from there up to their heads and
 *  the room kept over them. A bridge part standing in either band stops that part of the boat. */
export const HULL_LEVELS: Levels = { from: WORLD.waterY, to: WORLD.waterY + BOAT.freeboard };
export const CREW_LEVELS: Levels = {
  from: WORLD.waterY + BOAT.freeboard,
  to: WORLD.waterY + BOAT.eyeHeight + BOAT.headroom,
};

export interface BoatBarriers {
  readonly hull: readonly Obstacle[];
  readonly crew: readonly Obstacle[];
}

function reachesWater(site: SiteModel, ring: Ring): boolean {
  return (
    site.waterIndex.edgesNear(ringBounds(ring)).length > 0 || site.waterIndex.contains(ring[0]!)
  );
}

function barriers(id: string, rings: readonly Ring[]): Obstacle[] {
  return rings.map((polygon, i) => ({ id: `${id}-${i}`, kind: 'structure', polygon }));
}

/* What stands on a dam's crest stands over the water that runs under it, out of a boat's way. */
function onCrest(site: SiteModel, ring: Ring): boolean {
  return site.damDecks.some((deck) => ring.every((p) => pointInRing(p, deck)));
}

/** Everything a rowing boat cannot row through, in plan, at the height of its hull and over it. */
export function boatBarriers(input: BoatWorldInput): BoatBarriers {
  const { site, palette, landmarks, obstacles, afloat } = input;
  const bands = [HULL_LEVELS, CREW_LEVELS];
  const [bridgeLow, bridgeHigh] = bridgeBarriers(site.bridges, palette, bands);
  const [damLow, damHigh] = damBarriers(landmarks, bands);
  const gates = new Set(
    landmarks.filter((landmark) => landmark.kind === 'waterGate').map((gate) => gate.id)
  );
  const [gateLow, gateHigh] = waterGateBarriers(landmarks, bands, site.queries.baseHeightAt);
  return {
    hull: [
      ...obstacles.filter(
        (obstacle) =>
          !gates.has(obstacle.id) &&
          reachesWater(site, obstacle.polygon) &&
          !onCrest(site, obstacle.polygon)
      ),
      ...afloat,
      ...barriers('bridge-part', bridgeLow!),
      ...barriers('dam-part', damLow!),
      ...barriers('water-gate-part', gateLow!),
      // Walkers cross these on foot; a boat meets them as it meets a quay.
      ...barriers('water-access', site.waterAccessDecks ?? []),
    ],
    crew: [
      ...barriers('bridge-part-over', bridgeHigh!),
      ...barriers('dam-part-over', damHigh!),
      ...barriers('water-gate-part-over', gateHigh!),
    ],
  };
}

function rings(obstacles: readonly Obstacle[]): readonly Ring[] {
  return obstacles.map((obstacle) => obstacle.polygon);
}

/** The water a rowing boat can lie on: all the water of the site, less the banks, the moored
 *  hulls, the harbour's rows, and every part of a bridge or a dam standing in the way of its hull
 *  or its crew. A bridge or a dam is passed where its own mesh leaves the room, and nowhere else. */
export function buildBoatWater(input: BoatWorldInput): BoatWater {
  const { site } = input;
  const { bounds } = site;
  const standing = boatBarriers(input);
  const inHullBarrier = obstacleProbe(standing.hull);
  const inCrewBarrier = obstacleProbe(standing.crew);
  return createBoatWater({
    hullStations: BOAT.hullStations,
    crewStations: BOAT.crewStations,
    hull: createSlidingWorld({
      radius: BOAT.halfBeam,
      rings: [
        ...rings(standing.hull),
        ...site.water.flatMap((poly) => [poly.outer, ...poly.holes]),
        boundsRing(bounds),
      ],
      open(p: Vec2) {
        const inBounds =
          p[0] >= bounds.minX && p[0] <= bounds.maxX && p[1] >= bounds.minY && p[1] <= bounds.maxY;
        return inBounds && site.waterIndex.contains(p) && !inHullBarrier(p);
      },
    }),
    crew: createSlidingWorld({
      radius: BOAT.crewHalfWidth,
      rings: rings(standing.crew),
      open: (p: Vec2) => !inCrewBarrier(p),
    }),
  });
}
